/**
 * Phone + password login: one HTTP + session write path for all app UI versions.
 *
 * Consumer map:
 *   - `signInWithPhonePassword` → `version1|2|3` auth forms (`LoginSheetContent` / `SignInCredentialForm` on v2)
 *   - `authSessionApi` `login()`
 */
import { apiPost } from '../../api/client';
import { endpoints } from '../../config/endpoints';
import type { User } from '../../../data/domains/profile';
import { getAuthStore } from '../store';
import { persistSessionUser } from '../session/persistSessionUser';
import { setLoginJustHappened } from '../session/postLoginSignals';
import { extractLoginApiErrorMessage } from '../extractLoginApiErrorMessage';

export type SignInWithPhonePasswordResult =
  | { ok: true; user: User; unverified: boolean }
  | { ok: false; message: string };

/** Match legacy Ionic login JSON: `phone` is numeric local digits, `device_token` is FCM token from push registration. */
function loginPhoneForApi(phone: string): string | number {
  const trimmed = String(phone).trim();
  if (/^\d+$/.test(trimmed)) {
    const n = Number(trimmed);
    if (Number.isSafeInteger(n)) {
      return n;
    }
  }
  return phone;
}

export async function signInWithPhonePassword(
  phone: string,
  password: string,
  options: { device_token?: string; skipStoreUpdate?: boolean } = {},
): Promise<SignInWithPhonePasswordResult> {
  try {
    const response: unknown = await apiPost<unknown>(endpoints.login, {
      phone: loginPhoneForApi(phone),
      password,
      device_token: options.device_token ?? '',
    });
    const r = response as {
      success?: boolean;
      message?: string;
      data?: { customer?: User; user?: User; session_token?: string };
      customer?: User;
      user?: User;
      session_token?: string;
    };

    if (r && typeof r === 'object' && r.success === false) {
      return { ok: false, message: String(r.message || '') };
    }

    const data = r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : undefined;
    const customer = (data?.customer ?? data?.user ?? r.customer ?? r.user) as User | undefined;
    const token_from_top = (data?.session_token ?? r.session_token) as string | undefined;

    if (!customer || typeof customer !== 'object') {
      return { ok: false, message: r.message || 'Invalid login response' };
    }

    const token = (customer.session_token as string | undefined) ?? token_from_top;
    if (!token) {
      return { ok: false, message: r.message || 'No session token' };
    }

    const user_with_token = { ...customer, session_token: token } as User;
    const skip_store = options.skipStoreUpdate === true;
    await persistSessionUser(user_with_token);
    if (!skip_store) {
      setLoginJustHappened();
      getAuthStore().setUser(user_with_token);
    }
    const unverified = (customer as { is_verified?: boolean }).is_verified === false;
    return { ok: true, user: user_with_token, unverified };
  } catch (e: unknown) {
    const message = extractLoginApiErrorMessage(e, 'Network error');
    return { ok: false, message };
  }
}
